
import { useState } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { FORM_ENDPOINT } from '../../config/form';

// ===== お問い合わせページ 全面刷新（新デザイン憲章適用） =====
// 【重要】フォームの送信ロジック（送信先 FORM_ENDPOINT／フィールド名／バリデーション／fetch body／inquiryTypes の value）は
//   一切変更していない。見た目のみ刷新。由来: home/about の紺#1a2942・真鍮金・BTN-08・細罫スタイル。

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    inquiryType: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [formShownAt, setFormShownAt] = useState(() => Date.now());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    if (!formData.name || !formData.email || !formData.inquiryType || !formData.subject || !formData.message) {
      alert('必須項目を入力してください。');
      return;
    }

    if (formData.message.length > 500) {
      alert('お問い合わせ内容は500文字以内で入力してください。');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitErrorMessage('');

    if (!FORM_ENDPOINT) {
      setSubmitStatus('error');
      setSubmitErrorMessage('お問い合わせフォームは現在準備中です。お電話でお問い合わせください。');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({
          form: 'contact',
          lang: 'ja',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          category: formData.inquiryType,
          company: formData.company,
          subject: formData.subject,
          message: formData.message,
          _hp: '',
          page: window.location.href,
          userAgent: navigator.userAgent,
          formShownAt
        })
      });
      const result = await response.json() as { status?: string };

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success');
        // フォームをリセット
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          inquiryType: '',
          subject: '',
          message: ''
        });
        setFormShownAt(Date.now());
      } else {
        setSubmitStatus('error');
        setSubmitErrorMessage('送信に失敗しました。お電話（03-6824-4086）でお問い合わせください。');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitStatus('error');
      setSubmitErrorMessage('送信に失敗しました。お電話（03-6824-4086）でお問い合わせください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    { value: '', label: 'お問い合わせ種別を選択してください' },
    { value: 'membership', label: '会員募集について' },
    { value: 'qualification', label: '資格制度について' },
    { value: 'seminar', label: 'セミナー・イベントについて' },
    { value: 'corporate', label: '法人向けサービスについて' },
    { value: 'media', label: '取材・メディア関連' },
    { value: 'partnership', label: '提携・協力について' },
    { value: 'other', label: 'その他' }
  ];

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'お電話でのお問い合わせ',
      content: '03-6824-4086',
      subContent: '受付時間：年中無休',
      bgImage: '/images/generated/jba-fs-027-contact.jpg'
    },
    {
      icon: 'ri-mail-line',
      title: 'メールでのお問い合わせ',
      content: 'info@butler-association.jp',
      subContent: '24時間受付（返信は営業時間内）',
      bgImage: '/images/generated/jba-fs-028-contact-v6.jpg'
    },
    {
      icon: 'ri-map-pin-line',
      title: '事務局へのご来訪',
      content: '詳細はアクセス情報をご確認ください',
      subContent: '完全予約制',
      bgImage: '/images/generated/jba-fs-029-contact-v3.jpg'
    }
  ];

  const faqs = [
    { q: '執事資格を取得すれば、就職先を紹介してもらえますか？', a: '当協会は資格認定と教育を専門とする団体であり、人材紹介や派遣サービスは行っておりません。ただし、会員の方へ業界情報や求人情報を提供する場合がございます。就職活動はご自身で行っていただく形となります。' },
    { q: '未経験でも執事資格は取得できますか？', a: 'はい、可能です。Associate Butler（初級資格）は未経験の方を対象とした資格です。執事基礎講座などの入門セミナーから始められることをお勧めします。資格取得までの流れは「資格制度」ページをご確認ください。' },
    { q: '会員にならなくても資格試験は受験できますか？', a: '資格試験の受験に会員資格は必須ではありません。ただし、会員の方は受験料の割引やセミナー優待などの特典がございます。継続的に学ばれる方には会員登録をお勧めしております。' },
    { q: 'セミナーの録画視聴や資料のダウンロードはできますか？', a: '会員の方は、一部のセミナーについてアーカイブ動画の視聴や資料のダウンロードが可能です。非会員の方は各セミナーページをご確認いただくか、個別にお問い合わせください。' },
    { q: '法人として複数名の資格取得を支援したいのですが。', a: '法人会員制度をご用意しております。複数名での資格取得支援、企業研修プログラム、優先的なセミナー参加権などの特典がございます。詳細は「会員募集」ページをご確認いただくか、お電話にてご相談ください。' },
    { q: '本部への訪問は可能ですか？', a: '本部へのご来訪は完全予約制です。お電話（03-6824-4086）またはメール（info@butler-association.jp）にて事前にご予約をお願いいたします。年中無休ですが、ご希望の日時に対応できない場合もございますので、お早めにご連絡ください。' },
    { q: '海外在住ですが、資格取得は可能ですか？', a: '資格試験は原則として日本国内での実施となりますが、オンライン受験が可能な資格もございます。また、一部のセミナーはオンラインで開催しております。詳細は個別にお問い合わせください。' },
    { q: 'お問い合わせへの返信はどのくらいかかりますか？', a: 'メールでのお問い合わせには、2営業日以内にご返信いたします。お急ぎの場合はお電話（03-6824-4086）にてお問い合わせください。なお、内容によっては回答にお時間をいただく場合がございます。' },
  ];

  const accessFacts = [
    { icon: 'ri-map-pin-line', label: '所在地', value: '〒135-0064\n東京都江東区青海2丁目7−4' },
    { icon: 'ri-phone-line', label: '電話番号', value: '03-6824-4086' },
    { icon: 'ri-time-line', label: '営業時間', value: '年中無休（本部へのご来訪は完全予約制）\nお電話またはメールにてご予約をお願いいたします' },
    { icon: 'ri-train-line', label: '最寄駅', value: '東京国際クルーズターミナル駅より徒歩5分\nテレコムセンター駅より徒歩5分\n東京テレポート駅より徒歩15分' },
  ];

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'お問い合わせ' }
  ];

  const INPUT = "w-full px-4 py-3 border border-[#d8d2c4] bg-white focus:outline-none focus:border-[#a38e65] focus:ring-1 focus:ring-[#a38e65] transition-colors duration-300 text-sm font-['Noto_Serif_JP',serif] text-[#1a2942]";
  const LABEL = "block text-sm font-medium text-[#1a2942] mb-2 font-['Noto_Serif_JP',serif]";

  return (
    <Layout>
      {/* ===== ヒーロー — 全幅ビルボード型 ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[480px] lg:min-h-[540px] flex items-center">
        <img
          src="/images/generated/jba-fs-030-contact-v3.jpg"
          alt="来訪者を迎える執事協会事務局の応接空間"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.7)_50%,rgba(16,20,26,0.4)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[640px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Contact</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(26px,3.2vw,42px)] leading-[1.42] tracking-[0.04em] mb-6">
              お問い合わせ
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] mb-8">
              資格制度・セミナー・会員募集・法人向けサービス・取材など、ご質問やご相談を承っております。お電話・メール・フォームのいずれからでもお気軽にお声がけください。2営業日以内にご返信いたします。
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#contact-form" className="relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#f8f6f0] hover:text-[#1a2942] cursor-pointer whitespace-nowrap">お問い合わせフォーム</a>
              <a href="tel:03-6824-4086" className="inline-flex items-center justify-center gap-2 border border-[#a38e65] text-[#f8f6f0] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] whitespace-nowrap"><i className="ri-phone-line"></i>お電話でのご相談</a>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* ===== お問い合わせ方法 — 画像カード3種 ===== */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              ご都合に合わせて、<span className="text-[#a38e65]">三つの窓口</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              お電話・メール・ご来訪の三つの方法をご用意しています。お急ぎの場合はお電話が確実です。ご来訪は完全予約制となりますので、事前にご連絡をお願いいたします。
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {contactMethods.map((method) => (
              <div key={method.title} className="group relative overflow-hidden border border-[#e2ddd2]">
                <div className="relative h-72">
                  <img src={method.bgImage} alt={method.title} className="absolute inset-0 w-full h-full object-cover object-top" />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(16,20,26,0.94)_0%,rgba(16,20,26,0.55)_55%,rgba(16,20,26,0.2)_100%)]"></div>
                  <div className="absolute inset-0 p-7 flex flex-col justify-end text-left">
                    <i className={`${method.icon} text-[#d7b779] text-3xl mb-4`}></i>
                    <h3 className="jba-display font-medium text-[#f8f6f0] text-lg mb-2">{method.title}</h3>
                    <p className="font-['Playfair_Display',serif] text-[#f8f6f0] text-lg mb-1">{method.content}</p>
                    <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#c9c2b4]">{method.subContent}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== お問い合わせフォーム（送信ロジック不変・見た目のみ刷新）===== */}
      <section id="contact-form" className="py-14 sm:py-16 bg-[#f8f6f0]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="mb-10 text-center">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-[11px] text-[#8f7a56] mb-3">Inquiry Form</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-4">
              フォームからのお問い合わせ
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.9]">
              下記フォームにご記入のうえ送信してください。2営業日以内にご返信いたします。
            </p>
          </div>

          <div className="bg-white border border-[#e2ddd2] p-7 sm:p-10">
            <form onSubmit={handleSubmit} id="contact-form-element">
              <input type="hidden" name="formShownAt" value={formShownAt} />
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="name" className={LABEL}>お名前 <span className="text-[#a38e65]">*</span></label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className={INPUT} placeholder="山田 太郎" />
                </div>
                <div>
                  <label htmlFor="email" className={LABEL}>メールアドレス <span className="text-[#a38e65]">*</span></label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required className={INPUT} placeholder="example@email.com" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="phone" className={LABEL}>電話番号</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className={INPUT} placeholder="03-1234-5678" />
                </div>
                <div>
                  <label htmlFor="company" className={LABEL}>会社名・団体名</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} className={INPUT} placeholder="株式会社○○" />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="inquiryType" className={LABEL}>お問い合わせ種別 <span className="text-[#a38e65]">*</span></label>
                <div className="relative">
                  <select id="inquiryType" name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} required className={`${INPUT} pr-8 appearance-none cursor-pointer`}>
                    {inquiryTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#a38e65] pointer-events-none"></i>
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="subject" className={LABEL}>件名 <span className="text-[#a38e65]">*</span></label>
                <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleInputChange} required className={INPUT} placeholder="お問い合わせの件名をご記入ください" />
              </div>

              <div className="mb-6">
                <label htmlFor="message" className={LABEL}>お問い合わせ内容 <span className="text-[#a38e65]">*</span></label>
                <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required maxLength={500} rows={6} className={`${INPUT} resize-none`} placeholder="お問い合わせ内容を詳しくご記入ください（500文字以内）" />
                <div className="text-right text-xs text-[#8f7a56] mt-1 font-['Noto_Serif_JP',serif]">{formData.message.length}/500文字</div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-[#f0f4ee] border border-[#9bb08a]">
                  <div className="flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#3f5a34]">
                    <i className="ri-check-line text-lg"></i>
                    <p>お問い合わせを送信いたしました。2営業日以内にご返信いたします。</p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-[#f7ecec] border border-[#c98f8f]">
                  <div className="flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f3f3f]">
                    <i className="ri-error-warning-line text-lg"></i>
                    <p>{submitErrorMessage || '送信に失敗しました。お手数ですが、お電話（03-6824-4086）にてお問い合わせください。'}</p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  disabled={formData.message.length > 500 || isSubmitting}
                  className="relative inline-flex items-center justify-center gap-2 bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[52px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none"
                >
                  {isSubmitting ? (<><i className="ri-loader-4-line animate-spin"></i>送信中...</>) : (<><i className="ri-send-plane-line"></i>送信する</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ===== よくあるご質問 — 罫線リスト ===== */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-8 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              お問い合わせの前に、<span className="text-[#a38e65]">よくあるご質問</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              資格・受験・会員・ご来訪など、これまで多くいただいたご質問をまとめました。お問い合わせの前にご確認いただくと、より早く解決できる場合がございます。
            </p>
          </div>
          <div className="max-w-4xl border-t border-[#e2ddd2]">
            {faqs.map((f) => (
              <div key={f.q} className="border-b border-[#e2ddd2] py-6">
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.5] mb-3 flex items-start gap-3">
                  <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl leading-none pt-0.5">Q.</span><span>{f.q}</span>
                </h3>
                <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-base text-[#4a4638] leading-[1.95] pl-8">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== アクセス情報 — 定義リスト＋地図 ===== */}
      <section className="py-14 sm:py-16 bg-[#f8f6f0]">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-4xl mb-10 text-left">
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[30px] leading-[1.5] tracking-[0.02em] mb-5">
              事務局への<span className="text-[#a38e65]">アクセス</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#333] leading-[1.95]">
              事務局はお台場・青海にございます。ご来訪をご希望の際は、事前にご予約をお願いいたします。
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <div className="bg-white border border-[#e2ddd2] p-7 sm:p-9">
                <div className="mb-6">
                  <div className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">正式名称</div>
                  <div className="jba-display font-medium text-[#1a2942] text-lg">一般社団法人 全日本執事協会</div>
                  <div className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] mt-1">法人番号：2010005039584</div>
                </div>
                <dl className="border-t border-[#e2ddd2]">
                  {accessFacts.map((f) => (
                    <div key={f.label} className="flex gap-4 border-b border-[#e2ddd2] py-4">
                      <span className="w-9 h-9 shrink-0 border border-[#a38e65]/40 flex items-center justify-center text-[#a38e65]"><i className={f.icon}></i></span>
                      <div>
                        <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1">{f.label}</dt>
                        <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#1a2942] leading-[1.85] whitespace-pre-line">{f.value}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
                <div className="mt-6 p-4 bg-[#f8f6f0] border border-[#a38e65]/30">
                  <p className="font-['Noto_Serif_JP',serif] text-[13px] text-[#4a4638] leading-[1.85]">
                    <span className="text-[#1a2942] font-medium">ご来訪の際のご注意</span><br />
                    ご来訪は必ず事前予約が必要です。お電話またはメールにてご連絡ください。
                  </p>
                </div>
              </div>
            </div>
            <div className="border border-[#a38e65]/30 overflow-hidden">
              <iframe
                src="https://www.google.com/maps?q=35.6297,139.7745&output=embed"
                width="100%"
                height="460"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="全日本執事協会 事務局の地図"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
